import { z } from 'zod';

/**
 * Admin financial reports (ADR-013) — turnover / platform fee / therapist
 * earnings, sourced entirely from Booking rows already in Postgres (no
 * live Stripe API calls per page load). Revenue = CAPTURED payments only;
 * PENDING/AUTHORIZED/FAILED/AUTHORIZATION_CANCELLED are never counted, and
 * a REFUNDED booking's paymentStatus has already moved off CAPTURED by the
 * time it's refunded (see payments.repository.ts#markRefunded), so
 * refunds fall out of every query here automatically.
 *
 * Anonymity-first still applies: no client identity anywhere in this file.
 * Therapist identity IS included (therapists are not anonymous — ADR-008
 * §4 / ADR-009 §10), same narrow exception as the payout/admin therapist
 * summaries elsewhere.
 */

export const ReportGranularitySchema = z.enum(['month', 'year']);
export type ReportGranularity = z.infer<typeof ReportGranularitySchema>;

export const FinancialReportQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  granularity: ReportGranularitySchema.optional(),
});
export type FinancialReportQuery = z.infer<typeof FinancialReportQuerySchema>;

export const FinancialPeriodBucketSchema = z.object({
  /** e.g. "2026-08" for a month bucket, "2026" for a year bucket. */
  label: z.string(),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  grossTotal: z.string(),
  platformFeeTotal: z.string(),
  therapistNetTotal: z.string(),
  bookingCount: z.number().int().nonnegative(),
});
export type FinancialPeriodBucket = z.infer<typeof FinancialPeriodBucketSchema>;

export const FinancialOverviewResponseSchema = z.object({
  currency: z.string(),
  from: z.string().datetime(),
  to: z.string().datetime(),
  granularity: ReportGranularitySchema,
  grossTotal: z.string(),
  platformFeeTotal: z.string(),
  therapistNetTotal: z.string(),
  bookingCount: z.number().int().nonnegative(),
  byPeriod: z.array(FinancialPeriodBucketSchema),
});
export type FinancialOverviewResponse = z.infer<typeof FinancialOverviewResponseSchema>;

export const TherapistRevenueRowSchema = z.object({
  therapistUserId: z.string().cuid(),
  displayName: z.string().nullable(),
  grossTotal: z.string(),
  platformFeeTotal: z.string(),
  therapistNetTotal: z.string(),
  bookingCount: z.number().int().nonnegative(),
});
export type TherapistRevenueRow = z.infer<typeof TherapistRevenueRowSchema>;

export const TherapistRevenueListResponseSchema = z.object({
  currency: z.string(),
  from: z.string().datetime(),
  to: z.string().datetime(),
  therapists: z.array(TherapistRevenueRowSchema),
});
export type TherapistRevenueListResponse = z.infer<typeof TherapistRevenueListResponseSchema>;

/** No client identity — bookingId only, same boundary as invoice-export.ts (ADR-009 §10). */
export const CapturedSessionRowSchema = z.object({
  bookingId: z.string().cuid(),
  scheduledAt: z.string().datetime(),
  capturedAt: z.string().datetime().nullable(),
  status: z.string(),
  grossPrice: z.string(),
  platformFeeAmount: z.string(),
  therapistNetAmount: z.string(),
});
export type CapturedSessionRow = z.infer<typeof CapturedSessionRowSchema>;

export const TherapistSessionsResponseSchema = z.object({
  currency: z.string(),
  therapistUserId: z.string().cuid(),
  from: z.string().datetime(),
  to: z.string().datetime(),
  sessions: z.array(CapturedSessionRowSchema),
});
export type TherapistSessionsResponse = z.infer<typeof TherapistSessionsResponseSchema>;

export const FinancialExportScopeSchema = z.enum(['overview', 'therapists', 'sessions']);
export type FinancialExportScope = z.infer<typeof FinancialExportScopeSchema>;

export const FinancialExportQuerySchema = FinancialReportQuerySchema.extend({
  scope: FinancialExportScopeSchema,
  therapistUserId: z.string().cuid().optional(),
});
export type FinancialExportQuery = z.infer<typeof FinancialExportQuerySchema>;
